// packages/storefront/src/components/general/widgetLanguageNotice.tsx
//
// What the notice for a widget's unsupported `data-lang` says, without how it is drawn: its texts, the code as the page
// wrote it, and the table of every supported code. `WidgetLanguageAlert` draws it with MUI (the shop, the chat, the
// reviews widget); the terms widget, which has no MUI, draws its own copy (apps/embed/src/terms-widget). No MUI here.
import type { CSSProperties } from 'react';
import { supportedLocales } from '@tourtask/i18n/config';
import { ExactValue } from '@tourtask/ui/components/ExactValue';
import { LOCALE_CLIENT_META } from '@tourtask/utils/shared/i18n/localeClientMeta';
import type { WidgetName } from '@tourtask/storefront/translation/reportWidgetError';
import type { WidgetLanguageProblem } from '@tourtask/storefront/utils/widgetLanguage';

/**
 * The notice's two sentences, deliberately NOT translated (`WidgetLanguageAlert` says why): its title, and the line
 * before the table.
 */
export function widgetLanguageNoticeTexts(problem: WidgetLanguageProblem, widget: WidgetName): { title: string; instruction: string } {
	return {
		title: problem.kind === 'absent' ? 'This widget has no language code' : 'This widget’s language code is not supported',
		instruction:
			widget === 'chat'
				? 'Set the widget’s data-lang attribute to one of these codes:'
				: 'Set the widget’s data-lang attribute to one of these codes (on WordPress, the shortcode’s lang setting):',
	};
}

/** The attribute as the page wrote it, which a visitor's page translator leaves as it must be typed. */
export function GivenLanguageCode({ given }: { given: string }) {
	return <ExactValue style={GIVEN_CODE_STYLE}>{`data-lang="${given}"`}</ExactValue>;
}
const GIVEN_CODE_STYLE: CSSProperties = { fontFamily: 'monospace', overflowWrap: 'anywhere' };

/**
 * Inline, because the widget renders inside a partner's page whose theme styles bare tables (`.entry-content td`,
 * striped rows) with selectors that outrank a class; an inline style beats every host selector short of
 * `!important` (the same reason as `HostProofHeading`).
 */
const TABLE_STYLE: CSSProperties = {
	borderCollapse: 'collapse',
	border: 'none',
	margin: 0,
	width: 'auto',
	// A flex item of the alert's column: stretched, its two columns would spread across the whole alert.
	alignSelf: 'flex-start',
	backgroundColor: 'transparent',
	fontSize: 'inherit',
	lineHeight: 'inherit',
	color: 'inherit',
};
const ROW_STYLE: CSSProperties = { border: 'none', backgroundColor: 'transparent' };
const CELL_STYLE: CSSProperties = {
	padding: '2px 24px 2px 0',
	border: 'none',
	backgroundColor: 'transparent',
	textAlign: 'left',
	verticalAlign: 'top',
	fontSize: 'inherit',
	fontWeight: 'inherit',
	lineHeight: 'inherit',
	color: 'inherit',
};
const HEADER_CELL_STYLE: CSSProperties = { ...CELL_STYLE, fontWeight: 600 };
const CODE_STYLE: CSSProperties = { fontFamily: 'monospace' };

/** Every supported code and its language's English name, in `locales` order. */
export function SupportedLanguagesTable() {
	return (
		<table style={TABLE_STYLE}>
			<thead>
				<tr style={ROW_STYLE}>
					<th scope="col" style={HEADER_CELL_STYLE}>
						Code
					</th>
					<th scope="col" style={HEADER_CELL_STYLE}>
						Language
					</th>
				</tr>
			</thead>
			<tbody>
				{supportedLocales.map((code) => (
					<tr key={code} style={ROW_STYLE}>
						<td style={CELL_STYLE}>
							<ExactValue style={CODE_STYLE}>{code}</ExactValue>
						</td>
						<td style={CELL_STYLE}>{LOCALE_CLIENT_META[code].englishName}</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}
